/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getAllPlans, createPlan, updatePlan, deletePlan
} from '../../utils/api';

function PlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    billing_cycle: 'monthly',
    is_active: true,
    max_users: '',
    features: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const plansData = await getAllPlans();
      setPlans(plansData);
    }
    catch (err) {
      console.error('Erreur lors du chargement des plans:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      billing_cycle: 'monthly',
      is_active: true,
      max_users: '',
      features: '',
    });
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (plan) => {
    setCurrentPlan(plan);

    // Transform features from JSON back to comma-separated string
    let featuresString = '';
    if (plan.features) {
      if (typeof plan.features === 'string') {
        try {
          const featuresData = JSON.parse(plan.features);
          if (Array.isArray(featuresData)) {
            featuresString = featuresData.join(', ');
          }
          else if (typeof featuresData === 'object') {
            // Handle object format: {feature1: true, feature2: false, ...}
            featuresString = Object.keys(featuresData)
              .filter((key) => featuresData[key])
              .join(', ');
          }
          else {
            featuresString = plan.features;
          }
        }
        catch (e) {
          featuresString = plan.features;
        }
      }
      else if (Array.isArray(plan.features)) {
        featuresString = plan.features.join(', ');
      }
      else if (typeof plan.features === 'object') {
        // Handle object format directly
        featuresString = Object.keys(plan.features)
          .filter((key) => plan.features[key])
          .join(', ');
      }
    }

    setFormData({
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      billing_cycle: plan.billing_cycle,
      is_active: plan.is_active,
      max_users: plan.max_users || '',
      features: featuresString,
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (plan) => {
    setCurrentPlan(plan);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCreatePlan = async () => {
    try {
      await createPlan(formData);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Plan créé avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la création du plan:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la création du plan',
        severity: 'error',
      });
    }
  };

  const handleUpdatePlan = async () => {
    try {
      await updatePlan(currentPlan.plan_id, formData);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Plan mis à jour avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la mise à jour du plan:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la mise à jour du plan',
        severity: 'error',
      });
    }
  };

  const handleDeletePlan = async () => {
    try {
      await deletePlan(currentPlan.plan_id);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Plan supprimé avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la suppression du plan:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la suppression du plan',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3
      }}
      >
        <Typography variant="h4">
          Gestion des abonnements
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Ajouter un plan
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="table des plans">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Prix HT/véhicule</TableCell>
                <TableCell>Cycle de facturation</TableCell>
                <TableCell>Véhicules max</TableCell>
                <TableCell>Fonctionnalités</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans
                .slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
                .map((plan) => (
                  <TableRow
                    key={plan.plan_id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {plan.name}
                    </TableCell>
                    <TableCell>{plan.description || '-'}</TableCell>
                    <TableCell>{plan.price}€</TableCell>
                    <TableCell>
                      {plan.billing_cycle === 'monthly' ? 'Mensuel'
                        : plan.billing_cycle === 'yearly' ? 'Annuel'
                          : plan.billing_cycle}
                    </TableCell>
                    <TableCell>{plan.max_users || 'Illimité'}</TableCell>
                    <TableCell>
                      {(() => {
                        if (!plan.features) return '-';

                        if (typeof plan.features === 'string') {
                          try {
                            const featuresData = JSON.parse(plan.features);
                            if (Array.isArray(featuresData)) {
                              return featuresData.join(', ');
                            } if (typeof featuresData === 'object') {
                              return Object.keys(featuresData)
                                .filter((key) => featuresData[key])
                                .join(', ') || '-';
                            }
                          }
                          catch (e) {
                            return plan.features;
                          }
                        }

                        if (Array.isArray(plan.features)) {
                          return plan.features.join(', ');
                        }

                        if (typeof plan.features === 'object') {
                          return Object.keys(plan.features)
                            .filter((key) => plan.features[key])
                            .join(', ') || '-';
                        }

                        return String(plan.features);
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.is_active ? 'Actif' : 'Inactif'}
                        color={plan.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="info"
                        onClick={() => handleOpenEditDialog(plan)}
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(plan)}
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              {plans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Aucun plan trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={plans.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un nouveau plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="name"
                label="Nom du plan"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="price"
                label="Prix"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.price}
                onChange={handleInputChange}
                required
                InputProps={{
                  endAdornment: '€',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="billing_cycle"
                label="Cycle de facturation"
                fullWidth
                variant="outlined"
                value={formData.billing_cycle}
                onChange={handleInputChange}
                required
              >
                <MenuItem value="monthly">Mensuel</MenuItem>
                <MenuItem value="yearly">Annuel</MenuItem>
                <MenuItem value="quarterly">Trimestriel</MenuItem>
                <MenuItem value="biannual">Semestriel</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="max_users"
                label="Utilisateurs maximum"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.max_users}
                onChange={handleInputChange}
                helperText="Laisser vide pour illimité"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="is_active"
                label="Statut"
                fullWidth
                variant="outlined"
                value={formData.is_active}
                onChange={handleInputChange}
                required
              >
                <MenuItem value>Actif</MenuItem>
                <MenuItem value={false}>Inactif</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="features"
                label="Fonctionnalités (séparées par des virgules)"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.features}
                onChange={handleInputChange}
                helperText="Ex: module-facturation, module-planning, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleCreatePlan} variant="contained">Créer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="name"
                label="Nom du plan"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="price"
                label="Prix"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.price}
                onChange={handleInputChange}
                required
                InputProps={{
                  endAdornment: '€',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="billing_cycle"
                label="Cycle de facturation"
                fullWidth
                variant="outlined"
                value={formData.billing_cycle}
                onChange={handleInputChange}
                required
              >
                <MenuItem value="monthly">Mensuel</MenuItem>
                <MenuItem value="yearly">Annuel</MenuItem>
                <MenuItem value="quarterly">Trimestriel</MenuItem>
                <MenuItem value="biannual">Semestriel</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="max_users"
                label="Utilisateurs maximum"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.max_users}
                onChange={handleInputChange}
                helperText="Laisser vide pour illimité"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="is_active"
                label="Statut"
                fullWidth
                variant="outlined"
                value={formData.is_active}
                onChange={handleInputChange}
                required
              >
                <MenuItem value>Actif</MenuItem>
                <MenuItem value={false}>Inactif</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="features"
                label="Fonctionnalités (séparées par des virgules)"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.features}
                onChange={handleInputChange}
                helperText="Ex: module-facturation, module-planning, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleUpdatePlan} variant="contained">Mettre à jour</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Supprimer le plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer le plan <strong>{currentPlan?.name}</strong> ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleDeletePlan} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PlanManagement;
